import Auth from './auth'
import { getDefaultStorePrefixKey } from './defaults'
import { DummyAuth, DummyPerm } from './dummy'
import type {
  Authentication,
  AuthenticationStorage,
  LanguageHolder,
  Permission,
  PermissionStorage,
} from './interfaces'
import { normalizeRequestId } from './normalize'
import Perm from './perm'
import InternalStorage from './storage'
import type {
  AppConfig,
  AuthConfig,
  BaseUrlCallback,
  Env,
  LangCallback,
  PermConfig,
  RequestIdCallback,
  ServerLocation,
} from './types'

export type Storage = AuthenticationStorage & PermissionStorage

export interface Options {
  appConfig: AppConfig

  baseUrl?: string | BaseUrlCallback
  /** 环境 */
  env?: Env
  /** 服务器地址 */
  location?: ServerLocation
  /** 语言 */
  lang?: string | LangCallback | LanguageHolder
  /** 请求ID */
  requestId?: string | RequestIdCallback
  /** 本地存储前缀 */
  storagePrefixKey?: string
  /** 本地存储 */
  storage?: Storage
}

export default class SSO {
  public auth: Authentication

  public perm: Permission

  public storage: Storage

  constructor(opts: Options) {
    if (!opts.appConfig) {
      throw new TypeError('appConfig is required')
    }

    const appConfig = this.parseAppConfig(opts.appConfig)

    let storage

    if (opts.storage) {
      storage = opts.storage
    } else {
      const storagePrefixKey =
        opts.storagePrefixKey ?? getDefaultStorePrefixKey(appConfig.authCode || appConfig.permCode)

      storage = new InternalStorage({
        prefixKey: storagePrefixKey,
      })
    }

    const mergedOpts = {
      lang: opts.lang,
      env: opts.env,
      location: opts.location,
      baseUrl: opts.baseUrl,
      requestId: normalizeRequestId(opts.requestId),
      storage,
    } satisfies Omit<Options, 'appConfig'>

    this.auth = this.isAppAuthConfig(appConfig)
      ? new Auth({
          ...mergedOpts,
          appConfig,
        })
      : new DummyAuth()

    this.perm = this.isAppPermConfig(appConfig)
      ? new Perm({
          ...mergedOpts,
          appConfig,
        })
      : new DummyPerm()
    this.storage = storage
  }

  private parseAppConfig(config: AppConfig): AuthConfig & PermConfig {
    const appConfig = {
      authId: '',
      authCode: '',
      permCode: '',
      permKey: '',
    } satisfies AppConfig

    if (config.authId && config.authCode) {
      appConfig.authId = config.authId
      appConfig.authCode = config.authCode
    } else if (config.authId) {
      throw new TypeError('appConfig.authCode is required when appConfig.authId is provided')
    } else if (config.authCode) {
      throw new TypeError('appConfig.authId is required when appConfig.authCode is provided')
    }

    if (config.permCode && config.permKey) {
      appConfig.permCode = config.permCode
      appConfig.permKey = config.permKey
    } else if (config.permCode) {
      throw new TypeError('appConfig.permKey is required when appConfig.permCode is provided')
    } else if (config.permKey) {
      throw new TypeError('appConfig.permCode is required when appConfig.permKey is provided')
    }

    if (!this.isAppAuthConfig(appConfig) && !this.isAppPermConfig(appConfig)) {
      throw new TypeError('appConfig.authId or appConfig.permCode is required')
    }

    return appConfig
  }

  private isAppAuthConfig(config: AppConfig): config is AuthConfig {
    return !!config.authId && !!config.authCode
  }

  private isAppPermConfig(config: AppConfig): config is PermConfig {
    return !!config.permCode && !!config.permKey
  }
}
